export default {
  getCurrentAnswers(state) {
    // RMA-1549 - sometimes state.answers is null when this fires.
    const completedAnswers = state.answers.filter((answer) => answer.completed === true);

    if (completedAnswers && completedAnswers.length === 0) {
      return state.questions.slice(0, 0);
    }
    const lastAnswer = state.answers[completedAnswers.length - 1];

    const lastQuestion = state.questions.findIndex((question) => {
      return question.questionNumber === lastAnswer.questionNumber;
    });

    return state.questions.slice(0, lastQuestion + 1);
  },

  getNextUnansweredQuestion(state, getters) {
    return state.questions[getters.getCurrentAnswers.length];
  },

  getPreviewQuestion(state, getters) {
    return state.questions[getters.getCurrentAnswers.length + 1];
  },

  getFormattedReviewDetails(state, getters, rootState, rootGetters) {
    const {
      company,
      startYear,
      region,
      jobRoles,
      salaryFrequency,
      programmeLength,
      programme,
      provider,
      providerKnown,
      ...remainingJobDetails
    } = state.jobDetails;

    let companyFormatted = rootGetters['entities/entityValueToText']({
      entityType: 'companies',
      entityValues: company,
    });

    if (!companyFormatted) {
      companyFormatted = { name: remainingJobDetails.temp_comp_name };
    }

    let trainingProviderFormatted = rootGetters['entities/entityValueToText']({
      entityType: 'trainingProviders',
      entityValues: provider,
    });

    if (!trainingProviderFormatted) {
      trainingProviderFormatted = { name: remainingJobDetails.temp_training_provider_name };
    }

    if (remainingJobDetails.reviewLocations.length) {
      remainingJobDetails.temp_location_name = remainingJobDetails.reviewLocations[0].name;
    }

    return {
      ...remainingJobDetails,
      providerKnown: providerKnown === null ? null : !!providerKnown,
      company: companyFormatted,
      programme: rootGetters['entities/entityValueToText']({
        entityType: 'jobTypes',
        entityValues: programme,
      }),
      jobRoles: rootGetters['entities/entityValueToText']({
        entityType: 'jobRoles',
        entityValues: jobRoles,
        entityReturnType: 'Array',
      }),
      startYear: rootGetters['entities/entityValueToText']({
        entityType: 'startYears',
        entityValues: startYear,
      }),
      programmeLength: rootGetters['entities/entityValueToText']({
        entityType: 'jobLengths',
        entityValues: programmeLength,
      }),
      salaryFrequency: rootGetters['entities/entityValueToText']({
        entityType: 'salaryFrequencies',
        entityValues: salaryFrequency,
      }),
      region: rootGetters['entities/entityValueToText']({
        entityType: 'regions',
        entityValues: region,
      }),
      provider: trainingProviderFormatted,
    };
  },
  getReviewCompanyName: (state, getters, rootState, rootGetters) => (compId) => {
    return rootGetters['entities/getReviewCompanyName'](compId);
  },
  getFormattedJobDetailPayload: (state, getters, rootState) => ({ jobDetails }) => {
    const payload = {
      job_title: jobDetails.jobTitle,
      comp_id: jobDetails.company ? jobDetails.company.value : null,
      user_id: rootState.user.id,
      job_type_id: jobDetails.programme ? jobDetails.programme.value : null,
      jobRoles: jobDetails.jobRoles ? jobDetails.jobRoles.map((item) => item.value) : null,
      programme_start_year: jobDetails.startYear ? jobDetails.startYear.value : null,
      programme_length: jobDetails.programmeLength ? jobDetails.programmeLength.value : null,
      salary_frequency: jobDetails.salaryFrequency ? jobDetails.salaryFrequency.value : null,
      salary: jobDetails.salary,
      region: jobDetails.region ? jobDetails.region.value : null,
      training_provider_id: (jobDetails.provider === null) ? null : jobDetails.provider.value,
      training_provider_known: jobDetails.providerKnown,
      reviewLocations: jobDetails.reviewLocations,
      temp_location_name: jobDetails.temp_location_name,
    };

    if (!jobDetails.company.value) {
      payload.temp_comp_name = jobDetails.company.name;
    }

    if (jobDetails.provider !== null && !jobDetails.provider.value) {
      payload.temp_training_provider_name = jobDetails.provider.name;
    }

    return payload;
  },

  getJobDetailsPercentage: (state) => (review = state) => {
    const {
      company,
      temp_comp_name: tempCompanyName,
      provider,
      temp_training_provider_name: trainingProviderName,
      ...jobDetailFields
    } = review.jobDetails;

    // Note remove training provider fields from jobDetailsFields as they are not always required.

    const jobDetailsFieldsArray = Object.keys(jobDetailFields);

    if (jobDetailsFieldsArray.indexOf('jobDescription') > -1) {
      jobDetailsFieldsArray.splice(jobDetailsFieldsArray.indexOf('jobDescription'), 1);
    }
    const fieldLength = jobDetailsFieldsArray.length;

    let fieldResults = jobDetailsFieldsArray.reduce((accumulator, field) => {
      if (jobDetailFields[field] !== null) {
        accumulator += (state.jobDetailsPercent / fieldLength);
      }

      return accumulator;
    }, 0);

    if (company !== null || tempCompanyName !== null) {
      fieldResults += state.jobDetailsPercent / fieldLength;
    }
    return fieldResults;
  },

  getReviewQuestionsPercentage: (state) => (review = state) => {
    const ratedQuestions = review.questions.filter((question) => {
      const currentAnswer = review.answers.find((answer) => answer.questionNumber === question.questionNumber);
      if (!currentAnswer) {
        return false;
      }

      return (currentAnswer.rating !== null && question.validation.rating) || (!question.validation.rating && (currentAnswer.text !== null && currentAnswer.text.length > 0));
    });

    return (ratedQuestions.length / review.questions.length) * state.questionsPercent;
  },

  getUnfinishedReviewPercentage: (state) => (review = state) => {
    return review.completionPercentage;
  },

  getWordCountWarnings(state, getters) {
    const textQuestions = state.answers.filter((answer) => {
      // Look up related question for this answer
      const currentQuestion = state.questions.find((question) => answer.questionNumber === question.questionNumber);
      if (!currentQuestion) {
        return false;
      }

      return currentQuestion.inputType === 'text' && (answer.text === null || getters.getWordCount(answer.text) < currentQuestion.minWordLimit);
    });

    return textQuestions;
  },

  getWordCount: () => (answerText) => {
    if (answerText === null && answerText.length === 0) { return 0; }

    const matchCount = answerText.match(/\b[-?(\w+)?]+\b/gi);

    // TODO: probably being super defensive here and this check isn't needed. - Dom.L
    if (matchCount === null) return 0;

    return matchCount.length;
  },

  getFormattedQuestion: (state, getters, rootState, rootGetters) => (question) => {
    let companyName = rootGetters['entities/getReviewCompanyName'](state.jobDetails.compId);

    if (companyName === undefined && state.jobDetails.temp_comp_name !== null) {
      companyName = 'this company';
    } else if (companyName === undefined) {
      companyName = state.jobDetails.temp_comp_name;
    } else {
      companyName = companyName.name;
    }
    return question.replace(':company', companyName);
  },
};
